import React, { useEffect, useMemo, useState } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import {
  ArrowUpward,
  ArrowDownward,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import Paging from "./ActiveUserPaging";
import SummaryDetailsModal from "./SummaryDetailsModal";
import GetLinksModal from "./GetLinksModal";
import UserDialogModal from "./UserDialogModal";
import UserActionsModal from "./UserActionsModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { fetchWithToken } from "../../../api/apiutils";
import UserSubscription from "./UserSubscription";

function formatDateTime(dt) {
  if (!dt) return "Нет данных";
  const d = new Date(dt);
  return d.toLocaleString("ru-RU", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "Asia/Almaty",
  });
}

function formatDate(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  return d.toLocaleDateString("ru-RU", { timeZone: "Asia/Almaty" });
}

function renderSubscriptionPeriodHistory(history) {
  if (!Array.isArray(history) || history.length === 0) return "—";
  // Превратим в строки вида "dd.mm.yyyy - dd.mm.yyyy" и соединим через перенос строки
  return (
    <Box component="div" sx={{ whiteSpace: "pre-line" }}>
      {history
        .map((p) => `${formatDate(p.start)} - ${formatDate(p.end)}`)
        .join("\n")}
    </Box>
  );
}

const ActiveUserTable = ({
  data,
  loading,
  error,
  total,
  page,
  pageLimit,
  onPageChange,
  actionDateFrom,
  sortOrder = "default",
  onSortOrderChange,
}) => {
  useEffect(() => {
    console.log("what i need: ", page, pageLimit);
  }, [page, pageLimit]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [userDialogUserId, setUserDialogUserId] = useState(null);
  const [userDialogType, setUserDialogType] = useState(null);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [isGetLinksModalOpen, setIsGetLinksModalOpen] = useState(false);

  const [actionsModalOpen, setActionsModalOpen] = useState(false);
  const [actionsUserId, setActionsUserId] = useState(null);
  const [actionsType, setActionsType] = useState(null);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);

  const [subOpen, setSubOpen] = useState(false);
  const [userName, setUserName] = useState("");

  // БИН, по которому сейчас идёт поиск/создание анализа —
  // чтобы задизейблить повторные клики и показать спиннер именно на нужной кнопке
  const [analysingBin, setAnalysingBin] = useState(null);

  const rowsPerPage = pageLimit || 10;
  const navigate = useNavigate();

  /**
   * Сценарий по постановке:
   * 1. GET /api/companies/analyses?Identifier={БИН}&Page=1&PageLimit=1
   * 2. data пустой -> POST /api/companies/analyses, переходим на карточку,
   *    там уже покажут ожидание и будут опрашивать GET
   * 3. data не пустой -> смотрим data[0] и сразу переходим на карточку по его id.
   *    Готовность (isReady/isProcessing) и опрос статуса — забота самой карточки
   *    (BinActiveUserPage), сюда её логику дублировать не нужно.
   */
  const handleAnalyseAndNavigate = async (bin) => {
    if (analysingBin) return; // защита от повторных кликов, пока идёт запрос
    setAnalysingBin(bin);
    try {
      const searchResponse = await fetchWithToken(
        `/api/companies/analyses?Identifier=${bin}&Page=1&PageLimit=1`,
      );
      const searchResult = await searchResponse.json();

      if (searchResult?.data?.length) {
        const existing = searchResult.data[0];
        navigate(`/active-users/${bin}/${existing.id}`);
        return;
      }

      // По этому БИН анализов ещё нет — запускаем новый
      await createNewAnalysis(bin);
    } catch (error) {
      console.error("Ошибка при поиске существующего анализа:", error);
    } finally {
      setAnalysingBin(null);
    }
  };

  // Запуск нового анализа. Само по себе безопасно дёргать повторно —
  // бэкенд либо отдаст защищённый от дублей id (< 5 минут), либо готовый непротухший,
  // либо запустит расчёт заново.
  const createNewAnalysis = async (bin) => {
    const newItem = { identifier: bin, type: "OneTime" };
    try {
      const response = await fetchWithToken("/api/companies/analyses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newItem),
      });
      if (!response.ok) throw new Error(response.statusText);

      const data = await response.json();
      navigate(`/active-users/${bin}/${data.id}`);
    } catch (error) {
      console.error(error);
    }
  };

  function renderIdentifiers(item) {
    if (!item.identifiers.length) return <div>Не указано</div>;

    if (item.identifiers.length === 1) {
      return (
        <Button
          disabled={analysingBin === item.identifiers[0]}
          onClick={() => handleAnalyseAndNavigate(item.identifiers[0])}
        >
          {analysingBin === item.identifiers[0] ? (
            <CircularProgress size={16} />
          ) : (
            item.identifiers[0]
          )}
        </Button>
      );
    } else {
      return (
        <>
          {item.identifiers.map((id) => (
            <Button
              key={id}
              disabled={analysingBin === id}
              onClick={() => handleAnalyseAndNavigate(id)}
            >
              {analysingBin === id ? <CircularProgress size={16} /> : id}
            </Button>
          ))}
        </>
      );
    }
  }

  const sortedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (sortOrder === "asc")
      return [...data].sort(
        (a, b) => (a.countOfLikes || 0) - (b.countOfLikes || 0),
      );
    if (sortOrder === "desc")
      return [...data].sort(
        (a, b) => (b.countOfLikes || 0) - (a.countOfLikes || 0),
      );
    return data;
  }, [data, sortOrder]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / rowsPerPage)),
    [total, rowsPerPage],
  );

  const handleSortClick = () => {
    onSortOrderChange?.(
      sortOrder === "default"
        ? "asc"
        : sortOrder === "asc"
          ? "desc"
          : "default",
    );
  };

  const openActionsModal = (userId, type) => {
    setActionsUserId(userId);
    setActionsType(type); // может быть null
    setActionsModalOpen(true);
  };

  const handleButtonClick = (userId, type) => {
    setUserDialogUserId(userId);
    setUserDialogType(type);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setUserDialogUserId(null);
    setUserDialogType(null);
  };

  const handleViewResultClick = (id) => {
    setSelectedAnalysisId(id);
    setIsSummaryModalOpen(true);
  };

  const handleGetLinksClick = (id) => {
    setSelectedAnalysisId(id);
    setIsGetLinksModalOpen(true);
  };

  const handleSummaryModalClose = () => {
    setIsSummaryModalOpen(false);
    setSelectedAnalysisId(null);
  };

  const handleGetLinksModalClose = () => {
    setIsGetLinksModalOpen(false);
    setSelectedAnalysisId(null);
  };

  const handleAddSubscription = (userName) => {
    setUserName(userName);
    setSubOpen(true);
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">Ошибка: {error}</Typography>;

  return (
    <Box>
      <TableContainer component={Paper} className="user-list-container">
        <Typography variant="h6" gutterBottom>
          Общее количество пользователей: {total || 0}
        </Typography>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Номер телефона</TableCell>
              <TableCell>ОС</TableCell>
              <TableCell>Дата регистрации</TableCell>
              <TableCell>Дата последней активности</TableCell>
              <TableCell>Подписка</TableCell>
              <TableCell>Срок подписки</TableCell>
              <TableCell>БИН/ИИН</TableCell>
              <TableCell>История подписки</TableCell>
              <TableCell>
                <Box display="flex" alignItems="center">
                  Количество лайков
                  <IconButton onClick={handleSortClick} size="small">
                    {sortOrder === "asc" ? (
                      <ArrowUpward fontSize="small" />
                    ) : sortOrder === "desc" ? (
                      <ArrowDownward fontSize="small" />
                    ) : (
                      <RemoveIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell>Дизлайк</TableCell>
              <TableCell>Мои лайки</TableCell>
              <TableCell>Результат ТС</TableCell>
              <TableCell>Анализ</TableCell>
              <TableCell>Подборки</TableCell>
              <TableCell>Статистика платных фильтров</TableCell>
              <TableCell>Выгрузка эксель</TableCell>
              <TableCell>Поиск</TableCell>
              <TableCell>Активность</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.length ? (
              sortedData.map((item, index) => (
                <TableRow
                  key={item.user && item.user.id ? item.user.id : index}
                >
                  <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                  <TableCell
                    onClick={() => {
                      if (window.confirm("Добавить подписку?")) {
                        handleAddSubscription(item.userName || "Нет данных");
                      }
                    }}
                    sx={{ color: "#047bf3ff" }}
                  >
                    {item.userName || "Нет данных"}
                  </TableCell>

                  <TableCell>{item.operatingSystem}</TableCell>
                  <TableCell>
                    {item.dateOfRegistration
                      ? new Date(item.dateOfRegistration).toLocaleString(
                          "ru-RU",
                          {
                            dateStyle: "short",
                            timeStyle: "short",
                            timeZone: "Asia/Almaty",
                          },
                        )
                      : "Нет данных"}
                  </TableCell>
                  <TableCell>
                    {item.dateOfLastAction
                      ? new Date(item.dateOfLastAction).toLocaleString(
                          "ru-RU",
                          {
                            dateStyle: "short",
                            timeStyle: "short",
                            timeZone: "Asia/Almaty",
                          },
                        )
                      : "Нет данных"}
                  </TableCell>
                  <TableCell>{item.subscription || "Нет данных"}</TableCell>
                  <TableCell>
                    {item.subscriptionDate &&
                    item.subscriptionDate.start &&
                    item.subscriptionDate.end
                      ? `${new Date(
                          item.subscriptionDate.start,
                        ).toLocaleDateString()} - ${new Date(
                          item.subscriptionDate.end,
                        ).toLocaleDateString()}`
                      : "Нет данных"}
                  </TableCell>
                  <TableCell>{renderIdentifiers(item)}</TableCell>
                  <TableCell>
                    {renderSubscriptionPeriodHistory(
                      item.subscriptionPeriodHistory,
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() =>
                        handleButtonClick(
                          item.user && item.user.id
                            ? item.user.id
                            : item.userId,
                          "likes",
                        )
                      }
                    >
                      {item.countOfLikes || 0}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() => openActionsModal(item.user?.id, "Dislike")}
                    >
                      {item.countOfDislikes || 0}
                    </Button>
                  </TableCell>

                  <TableCell>
                    <Button
                      onClick={() =>
                        openActionsModal(item.user?.id, "Favourite")
                      }
                    >
                      {item.countOfFavourites || 0}
                    </Button>
                  </TableCell>

                  <TableCell>
                    <Button
                      onClick={() =>
                        openActionsModal(item.user?.id, "FileData")
                      }
                    >
                      {item.countOfTSData || 0}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() =>
                        handleButtonClick(
                          item.user && item.user.id
                            ? item.user.id
                            : item.userId,
                          "analysis",
                        )
                      }
                    >
                      {item.countOfAnalysis || 0}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() =>
                        handleButtonClick(
                          item.user && item.user.id
                            ? item.user.id
                            : item.userId,
                          "selection",
                        )
                      }
                    >
                      {item.countOfSelection || 0}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() =>
                        handleButtonClick(
                          item.user && item.user.id
                            ? item.user.id
                            : item.userId,
                          "paidfilter",
                        )
                      }
                    >
                      {item.countOfPaidFilters || 0}
                    </Button>
                  </TableCell>
                  <TableCell>{item.countOfExports || 0}</TableCell>
                  <TableCell>
                    <Button
                      onClick={() =>
                        handleButtonClick(
                          item.user && item.user.id
                            ? item.user.id
                            : item.userId,
                          "textFilters",
                        )
                      }
                    >
                      {item.countOfTextFilters || 0}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() => openActionsModal(item.user?.id, null)}
                    >
                      {item.activityLevelName || "Нет данных"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={18} align="center">
                  Нет данных
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <UserSubscription
        open={subOpen}
        onClose={() => setSubOpen(false)}
        userName={userName}
      />

      <Paging
        localPage={page}
        totalPages={totalPages}
        onPageChange={onPageChange}
      />

      <UserDialogModal
        open={dialogOpen}
        onClose={handleDialogClose}
        userId={userDialogUserId}
        type={userDialogType}
        onViewResultClick={handleViewResultClick}
        onGetLinksClick={handleGetLinksClick}
        actionDateFrom={actionDateFrom}
      />

      <UserActionsModal
        open={actionsModalOpen}
        onClose={() => setActionsModalOpen(false)}
        userId={actionsUserId}
        actionDateFrom={actionDateFrom}
        actionType={actionsType}
      />

      <SummaryDetailsModal
        open={isSummaryModalOpen}
        onClose={handleSummaryModalClose}
        analysisId={selectedAnalysisId}
      />
      <GetLinksModal
        open={isGetLinksModalOpen}
        onClose={handleGetLinksModalClose}
        analysisId={selectedAnalysisId}
      />
    </Box>
  );
};

export default ActiveUserTable;
